import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type LegalType = 'privacy' | 'terms' | null;
interface LegalModalProps {
  type: LegalType;
  onClose: () => void;
}
export function LegalModal({
  type,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    if (type) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [type, onClose]);
  const content = type ? legalContent[type] : null;
  return <AnimatePresence data-id="element-201">
      {type && content && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} data-id="element-202">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-soft-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-203" />

          {/* Dialog */}
          <motion.div role="dialog" aria-modal="true" aria-labelledby="legal-title" initial={{
        opacity: 0,
        y: 30,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} transition={{
        duration: 0.3
      }} className="relative w-full max-w-[680px] max-h-[85vh] bg-white rounded-3xl shadow-luxury-card border-t-[6px] border-t-champagne-gold flex flex-col overflow-hidden" data-id="element-204">
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-10 pt-8 pb-4 border-b border-[#E8E8F0]" data-id="element-205">
              <h2 id="legal-title" className="font-playfair font-bold text-deep-blue text-2xl md:text-[28px]" data-id="element-206">
                {content.title}
              </h2>
              <button onClick={onClose} aria-label="Cerrar" className="shrink-0 text-gray-text hover:text-deep-blue transition-colors p-2 rounded-full hover:bg-pearl-white" data-id="element-207">
                <X size={22} data-id="element-208" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 md:px-10 py-6 overflow-y-auto" data-id="element-209">
              {content.sections.map((section, i) => <div key={i} className="mb-6 last:mb-0" data-id="element-210">
                  {section.heading && <h3 className="font-montserrat font-semibold text-deep-blue text-base mb-2" data-id="element-211">
                      {section.heading}
                    </h3>}
                  <p className="font-montserrat text-gray-text text-sm leading-[1.8]" data-id="element-212">
                    {section.body}
                  </p>
                </div>)}
              <p className="font-montserrat font-light text-gray-text/70 text-xs mt-8 italic" data-id="element-213">
                Última actualización: enero 2024
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 md:px-10 py-5 border-t border-[#E8E8F0] flex justify-end" data-id="element-214">
              <button onClick={onClose} className="bg-cta-gradient text-white font-montserrat font-semibold text-sm py-2.5 px-7 rounded-full hover:scale-105 transition-transform duration-300" data-id="element-215">
                Entendido
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
const legalContent: Record<'privacy' | 'terms', {
  title: string;
  sections: {
    heading?: string;
    body: string;
  }[];
}> = {
  privacy: {
    title: 'Política de Privacidad',
    sections: [{
      body: 'En RenuevaTE_90 valoramos profundamente tu confianza. Esta política explica cómo cuidamos y protegemos la información que decides compartir con nosotras.'
    }, {
      heading: 'Información que recopilamos',
      body: 'Recopilamos únicamente la información que tú nos proporcionas de forma voluntaria a través de nuestro formulario, como tu nombre, edad, ciudad y tu historia de transformación. Nunca solicitamos información que no sea necesaria para nuestra comunidad.'
    }, {
      heading: 'Cómo usamos tu información',
      body: 'Tu historia se utiliza exclusivamente con tu autorización expresa, y solo para inspirar a otras mujeres dentro de la comunidad RenuevaTE_90, en materiales educativos, redes sociales y comunicaciones de la comunidad. Tú decides qué se comparte y cómo apareces.'
    }, {
      heading: 'Tu control y tus derechos',
      body: 'Tienes el control total de tu información en todo momento. Puedes solicitar que tu historia se publique de forma anónima, con tu nombre de pila, o no se publique en absoluto. También puedes retirar tu autorización en cualquier momento, sin necesidad de dar explicaciones.'
    }, {
      heading: 'Protección de tus datos',
      body: 'Nunca vendemos, alquilamos ni compartimos tu información con terceros con fines comerciales. Tu información se almacena de forma segura y confidencial.'
    }]
  },
  terms: {
    title: 'Términos y Condiciones',
    sections: [{
      body: 'Al participar en la comunidad RenuevaTE_90 y compartir tu historia, aceptas los siguientes términos. Te invitamos a leerlos con calma.'
    }, {
      heading: 'Propósito de esta página',
      body: 'Esta página tiene fines informativos y de comunidad. Su objetivo es invitar a mujeres de 40 años o más a compartir su historia de transformación para inspirar a otras. No es una página de venta de productos.'
    }, {
      heading: 'Naturaleza de las historias',
      body: 'Las historias compartidas reflejan experiencias personales reales. Los resultados pueden variar de persona a persona y no constituyen una promesa ni garantía de resultados específicos.'
    }, {
      heading: 'No reemplaza asesoría médica',
      body: 'El contenido de esta página y de la comunidad RenuevaTE_90 es educativo y de acompañamiento. No reemplaza la asesoría, diagnóstico o tratamiento médico profesional. Consulta siempre con tu médico antes de iniciar cualquier programa de nutrición o ejercicio.'
    }, {
      heading: 'Uso autorizado del contenido',
      body: 'El contenido, marca, logotipo y materiales de RenuevaTE_90 y Saludable sin ser Esclavo están protegidos. Su uso sin autorización está prohibido. Las historias compartidas solo se utilizan con el consentimiento expreso de quien las aporta.'
    }]
  }
};